import { nameFromNumber, numberFromName } from "@/data/numbers";
import { IUnit, IUpgradeOption, IUpgradePackage, IUpgradeSection } from "@/data/types";
import { groupBy } from "lodash-es";
import { nanoid } from "nanoid";
import ArmyBookGain from "./armyBook/ArmyBookGain";
import ArmyBookUpgradeOption from "./armyBook/ArmyBookUpgradeOption";
import UnitService from "./UnitService";

export default class DataParsingService {
  public static transformApiData(input, fallback?: (err: string) => void) {
    try {
      const upgradePackages: IUpgradePackage[] = this.transformPackages(input.upgradePackages);

      const units: IUnit[] = input.units.map((unit: IUnit) => UnitService.fixUnit(unit));

      const data = {
        ...input,
        units,
        upgradePackages
      };

      for (let unit of data.units) {
        // Group weapons by name
        const groups = groupBy(unit.weapons, "name");

        // Take first weapon in each group, with a count set to how many are in the group
        unit.weapons = (Object.values(groups) as any[]).map((group: any[]) => {
          const countInGroup = group.reduce((count, next) => count + (next.count ?? 1), 0);
          return {
            ...group[0],
            count: countInGroup,
            originalCount: countInGroup
          };
        });
      }

      return data;
    } catch (err) {
      console.error(err);
    }
  }

  public static transformPackages(upgradePackages) {
    return upgradePackages.map((pkg) => {
      return {
        ...pkg,
        sections: pkg.sections.map((section) => {
          let sectionLabel = section.label;

          if (sectionLabel?.length > 0) {
            if (!section.label.endsWith(":")) {
              sectionLabel += ":";
            }
          } else {
            //console.error(`Section label ${sectionLabel} is undefined`, JSON.stringify(section));
          }

          const upgrade = this.parseUpgradeText(sectionLabel);

          // Sanitise dodgy/old data
          delete section.select;
          delete section.affects;
          delete section.targets;

          return {
            id: section.uid ?? section.id,
            ...section,
            ...upgrade,
            label: this.upgradeToString(upgrade),
            options: section.options.map((opt: IUpgradeOption) => {
              // Group/combine gains with same name...
              const gainsGroups = groupBy(opt.gains, (x) => ArmyBookGain.Parse(x).toString());
              const parsed = ArmyBookUpgradeOption.FromObject(opt);

              delete (opt as any).proposedCost;
              delete (opt as any).proposedCostHint;
              delete (opt as any).proposedVersion;

              return {
                ...opt,
                parentSectionId: section.uid,
                //cost: cost,
                id: opt.uid || (opt as any).id,
                label: parsed.toString(),
                // Group same items back together and sum the count
                gains: (Object.values(gainsGroups) as any[]).map((grp: any[]) => {
                  const count = grp.reduce((c, next) => c + (next.count || 1), 0);
                  return {
                    ...grp[0],
                    label: ArmyBookGain.Parse(grp[0]).toString(),
                    count: count
                  };
                })
              };
            })
          };
        })
      };
    });
  }

  public static parseUpgradeText(text: string): IUpgradeSection {
    try {
      if (!text.endsWith(":")) {
        text = text + ":";
      }

      if (/mount on/i.exec(text)) {
        return {
          variant: "upgrade",
          type: "ArmyBookUpgradeSection"
        };
      }

      const getSelectOrAffects = (val, upto?) => {
        return (
          ["any", "all"].includes(val.toLowerCase())
            ? { type: val.toLowerCase() }
            : {
                type: upto ? "up to" : "exactly",
                value: parseInt(val) || numberFromName(val.toLowerCase())
              }
        ) as { type: "any" | "all" | "exactly" | "up to"; value?: number };
      };

      const takeMatch =
        /^Take(?:\sup to)?\s([\d]+|one|two|three|four|five|six|seven|eight|nine|ten|any)?\s?(.+?)\s(attachment|upgrade)s?:?/.exec(
          text
        );
      if (takeMatch)
        return {
          id: nanoid(7),
          type: "ArmyBookUpgradeSection",
          variant: "attachment",
          select: getSelectOrAffects(takeMatch[1]),
          targets: [takeMatch[2]]
        };

      // /^(?<select>any|one) model may (?<type>replace|take) (?<upto>up to )?(?<affects>any|one|two|three|four|five|six|all)?\s?(?<targets>.+?)(?: attachments?)?:/
      // /^(?<select>any|one) model may (?<type>replace|take) (?<upto>up to )?(?<affects>any|one|two|three|four|five|six|all)?\s?(?<targets>.+?)(?: attachments?)?:/
      const anyModelMatch =
        /^(?<select>any|one) model may (?<type>replace|take) (?<upto>up to )?(?<affects>any|one|two|three|four|five|six|seven|eight|nine|ten|all)?\s?(?<targets>.+?)(?: attachments?)?:/gi.exec(
          text
        );

      if (anyModelMatch) {
        const affects = anyModelMatch.groups["affects"];
        return {
          id: nanoid(7),
          type: "ArmyBookUpgradeSection",
          variant: anyModelMatch.groups["type"] === "replace" ? "replace" : "attachment",
          select: getSelectOrAffects(anyModelMatch.groups["select"]),
          affects: affects ? getSelectOrAffects(affects, anyModelMatch.groups["upto"]) : undefined,
          model: true,
          targets: [anyModelMatch.groups["targets"]]
        };
      }

      // Honestly I just don't want to change the monster below...
      const upgradeUpToModelsMatch =
        /Upgrade up to (?<affects>\d+|one|two|three|four|five|six|seven|eight|nine|ten) models with\s?(?<select>\d+|one|two|three|four|five|six|seven|eight|nine|ten)?/i.exec(
          text
        );
      if (upgradeUpToModelsMatch) {
        const affects = upgradeUpToModelsMatch.groups.affects;
        const select = upgradeUpToModelsMatch.groups.select;
        return {
          id: nanoid(7),
          type: "ArmyBookUpgradeSection",
          variant: "upgrade",
          model: true,
          affects: {
            type: "up to",
            value: parseInt(affects) || numberFromName(affects)
          },
          select: {
            type: "exactly",
            value: parseInt(select) || numberFromName(select)
          }
        };
      }

      const upgradeUpToOfAnyMatch =
        /Upgrade with (?<selectType>any|up to) (?<selectValue>\d+|one|two|three|four|five|six|seven|eight|nine|ten|number)?\s?of any/i.exec(
          text
        );
      if (upgradeUpToOfAnyMatch) {
        const selectType = upgradeUpToOfAnyMatch.groups.selectType as "any" | "up to";
        const selectValue = upgradeUpToOfAnyMatch.groups.selectValue;
        return {
          id: nanoid(7),
          type: "ArmyBookUpgradeSection",
          variant: "upgrade",
          affects: {
            type: "any"
          },
          select: {
            type: selectType,
            value: selectType === "up to" ? parseInt(selectValue) || numberFromName(selectValue) : undefined
          }
        };
      }

      text = text.endsWith(":") ? text.substring(0, text.length - 1) : text;

      let match =
        /(?<type>Upgrade|Replace)\s?(?<affectsUpTo>up to\s)?(?<affects>any|one|two|three|four|five|six|seven|eight|nine|ten|all)?\s?(?:models?)?(?:(?<upgradeWhat>.+)\swith)?\s?(?:with)?\s?(?<select>one|any)?(?:up to (?<selectupto>.+?)(?:\s|$))?(?<targets>.+)?/.exec(
          text
        );

      if (!match) {
        //console.error("Cannot match: " + text);
        return {
          id: nanoid(7),
          type: "ArmyBookUpgradeSection",
          variant: "upgrade"
        };
      }

      const targets = match.groups.targets;

      const result: IUpgradeSection = {
        id: nanoid(7),
        type: "ArmyBookUpgradeSection",
        variant: match.groups.type.toLowerCase() as any,
        model: text.indexOf("model") > -1
      };

      if (match.groups.affects) {
        result.affects = getSelectOrAffects(match.groups.affects, match.groups.affectsUpTo);
      }

      if (match.groups.selectupto) {
        result.select = getSelectOrAffects(match.groups.selectupto, true);
      } else if (match.groups.select) {
        result.select = getSelectOrAffects(match.groups.select);
      }

      if (match.groups.upgradeWhat) result.targets = [match.groups.upgradeWhat];

      if (targets) {
        // has alternative replave options like "Replace one R-Carbine and CCW / G-Rifle and CCW:"
        if (targets.indexOf("/") > -1) {
          result.targets = targets
            .split("/")
            .map((part) => part.trim())
            .map((part) => part.split(" and "))
            .map((part) => part.map((p) => p.split(", ")).reduce((arr, next) => arr.concat(next), [])) as any;
        } else {
          result.targets = targets
            .split(" and ")
            .map((part) => part.split(", "))
            .reduce((arr, next) => arr.concat(next), []);
        }
      }

      // TODO: Better way of doing this?
      // if (
      //   result.variant === "upgrade" &&
      //   result.targets &&
      //   !result.affects &&
      //   !result.select &&
      //   !result.model
      // )
      //   result.variant = "upgradeRule";

      if (result.model === false) {
        delete result.model;
      }

      return result;
    } catch {
      //console.error("Could not parse upgrade: ", text);
      throw new Error("Could not parse upgrade: " + text);
    }
  }

  public static upgradeToString(upgrade: IUpgradeSection) {
    switch (upgrade.variant) {
      case "replace":
        return this.replaceUpgradeToString(upgrade);
      case "upgrade":
        return this.upgradeUpgradeToString(upgrade);
      case "attachment":
        return this.attachmentUpgradeToString(upgrade);
    }
  }

  private static replaceUpgradeToString(upgrade: IUpgradeSection) {
    const affects = this.upgradeAffectsToString(upgrade);
    const targets = this.upgradeTargetsToString(upgrade);
    const select = this.upgradeSelectToString(upgrade);

    const result = `replace ${affects} ${targets}`.replace(/\s\s/g, " ");

    if (upgrade.model) {
      return capitalise(`${select} model may ` + result);
    }

    return capitalise(result);
  }

  private static upgradeUpgradeToString(upgrade: IUpgradeSection) {
    const affects = this.upgradeAffectsToString(upgrade);
    const targets = this.upgradeTargetsToString(upgrade);
    const select = this.upgradeSelectToString(upgrade);

    if (upgrade.model) {
      return `Upgrade ${affects} model${
        upgrade.affects?.type === "all" || upgrade.affects?.value > 1 ? "s" : ""
      } ${select ? "with " + select : "with"}`;
    }

    if (affects && !select) {
      if (targets) {
        return `Upgrade ${affects} ${targets} with`;
      }

      return "Upgrade with " + affects;
    }

    if (select && !affects) {
      return `Upgrade ${targets || ""} with ${select}`.replace(/\s\s/g, " ");
    }

    if (!targets && select && affects === "any") {
      return `Upgrade with ${select === "any" ? "any number" : select} of any`;
    }

    return `Upgrade ${affects || ""} ${targets || ""} ${select ? "with " + select : "with"}`.replace(
      /\s+/g,
      " "
    );
  }

  private static attachmentUpgradeToString(upgrade: IUpgradeSection) {
    const affects = this.upgradeAffectsToString(upgrade);
    const targets = this.upgradeTargetsToString(upgrade);
    const select = this.upgradeSelectToString(upgrade);
    const result = `${upgrade.model ? select + " model may " : ""}take ${
      upgrade.model ? affects : select
    } ${targets} attachment`;
    return capitalise(result);
  }

  private static upgradeAffectsToString(upgrade: IUpgradeSection) {
    if (!upgrade.affects) return "";
    if (upgrade.affects.type === "up to") return "up to " + nameFromNumber(upgrade.affects.value);
    if (upgrade.affects.type === "exactly")
      return upgrade.model
        ? nameFromNumber(upgrade.affects.value)
        : upgrade.affects.value === 1
          ? "one"
          : upgrade.affects.value + "x";
    return upgrade.affects.type;
  }

  private static upgradeTargetsToString(upgrade: IUpgradeSection) {
    return upgrade.targets?.join(" and ");
  }

  private static upgradeSelectToString(upgrade: IUpgradeSection) {
    if (!upgrade.select) return "";
    if (upgrade.select.type === "up to") return "up to " + nameFromNumber(upgrade.select.value);
    if (upgrade.select.type === "exactly")
      return upgrade.select.value === 1 ? "one" : upgrade.select.value + "x";
    return upgrade.select.type;
  }

  // public static parseRule(r): ISpecialRule | IUpgradeGainsRule {
  //   const defenseMatch = /^(Defense) \+(\d+)\s?(in melee)?/.exec(r);
  //   if (defenseMatch) {
  //     return {
  //       key: defenseMatch[1].toLowerCase().replace(/\s+/g, "-"),
  //       name: defenseMatch[1],
  //       rating: defenseMatch[2] || "",
  //       condition: defenseMatch[3] || "",
  //     };
  //   }
  //   const rMatch = /^(.+?)(?:\((\+?)(\d+)\))?( in melee)?$/.exec(r);
  //   const result = {
  //     key: rMatch[1].toLowerCase().replace(/\s+/g, "-"),
  //     name: rMatch[1],
  //     rating: rMatch[3] || "",
  //     modify: rMatch[2] === "+",
  //     condition: rMatch[4] ? rMatch[4].trim() : null,
  //   };
  //   if (!result.condition) delete result.condition;
  //   if (!result.modify) delete result.modify;
  //   return result;
  // }
}

function capitalise(value) {
  if (!value) return "";
  value = value.toString();
  return value.charAt(0).toUpperCase() + value.slice(1);
}
